import React from 'react';
import { Lock, MessageCircle, Calendar, Package } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
interface AuthGateProps {
  isOpen: boolean;
  onClose: () => void;
  onSignIn: () => void;
  action: 'book' | 'chat' | 'list' | 'activity';
}
const actionConfig = {
  book: {
    icon: Calendar,
    title: 'Sign in to Book',
    description: 'Create an account to request bookings and manage your rentals.',
    benefits: ['Request items from verified owners', 'Secure payment processing', 'Track your rental history']
  },
  chat: {
    icon: MessageCircle,
    title: 'Sign in to Chat',
    description: 'Create an account to message owners and ask questions about items.',
    benefits: ['Ask questions before booking', 'Coordinate pickup details', 'Build trust with the community']
  },
  list: {
    icon: Package,
    title: 'Sign in to List Items',
    description: 'Create an account to share your items and start earning.',
    benefits: ['List unlimited items', 'Set your own prices', 'Earn money from what you own']
  },
  activity: {
    icon: Calendar,
    title: 'Sign in to View Activity',
    description: 'Create an account to track your bookings and rental history.',
    benefits: ['Manage active rentals', 'View booking history', 'Track your earnings']
  }
};
export function AuthGate({
  isOpen,
  onClose,
  onSignIn,
  action
}: AuthGateProps) {
  const config = actionConfig[action];
  const Icon = config.icon;
  return <AnimatePresence data-id="element-47">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" data-id="element-48" />

          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 p-6 pb-8" data-id="element-49">
            <div className="flex justify-center mb-6" data-id="element-50">
              <div className="w-16 h-16 bg-dark-green/10 rounded-full flex items-center justify-center" data-id="element-51">
                <Icon size={32} className="text-dark-green" data-id="element-52" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-charcoal text-center mb-3" data-id="element-53">
              {config.title}
            </h2>
            <p className="text-moss-gray text-center mb-6 leading-relaxed" data-id="element-54">
              {config.description}
            </p>

            <div className="bg-sand/20 rounded-ios-lg p-4 mb-6" data-id="element-55">
              <p className="text-sm font-semibold text-charcoal mb-3" data-id="element-56">
                With an account you can:
              </p>
              <ul className="space-y-2" data-id="element-57">
                {config.benefits.map((benefit, index) => <li key={index} className="flex items-start gap-2 text-sm text-charcoal" data-id="element-58">
                    <span className="text-dark-green mt-0.5" data-id="element-59">✓</span>
                    <span data-id="element-60">{benefit}</span>
                  </li>)}
              </ul>
            </div>

            <div className="space-y-3" data-id="element-61">
              <Button variant="primary" fullWidth size="lg" onClick={onSignIn} data-id="element-62">
                Sign In or Create Account
              </Button>
              <Button variant="secondary" fullWidth onClick={onClose} data-id="element-63">
                Continue Browsing
              </Button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}